<template>
  <DeepDialog
    v-model="showDialog"
    :fullscreen="mdAndDown"
    fullscreen-on-mobile
    :max-width="900"
    :title="i18n.t('texts.customize_signature')"
    @keydown.esc="() => (showDialog = false)"
  >
    <template #content>
      <div class="text-black text-body-large mb-4">
        {{ i18n.t('settings.subtitles.my_signature') }}
      </div>

      <v-row class="py-4">
        <v-col class="mt-4 order-md-2" cols="12" md="4">
          <v-responsive
            ref="responsiveWrapper"
            :aspect-ratio="851 / 448"
            class="w-100"
          >
            <v-sheet
              class="border border-sm bg-grey-lighten-4 pa-4 d-flex w-100 h-100 align-center justify-center"
              color="grey-lighten-4"
              rounded="lg"
            >
              <div class="position-relative w-100 h-100">
                <div
                  v-if="preview.isReady && !preview.state.value"
                  class="preview-wrapper rounded border border-sm bg-white"
                ></div>
                <div>
                  <img
                    v-if="preview.state.value"
                    :alt="i18n.t('settings.labels.signature_preview')"
                    class="signature-preview-image mx-auto w-100 rounded"
                    :src="preview.state.value"
                  />
                  <OverlaySpinner
                    v-if="preview.state.value && preview.isLoading.value"
                  />
                </div>
              </div>
            </v-sheet>
          </v-responsive>
          <div
            v-if="preview.isReady && !preview.error.value"
            class="text-center text-body-medium mx-auto py-4"
          >
            {{ i18n.t('settings.labels.signature_preview') }}
          </div>
        </v-col>

        <v-col class="order-md-1" cols="12" md="8">
          <v-form ref="userSignatureForm">
            <div class="d-flex flex-column ga-8">
              <div>
                <label class="d-block text-body-medium mb-2">
                  {{ i18n.t('components.signaturePad.signature') }}
                </label>
                <div id="settings-signature-pad">
                  <DeepSignature
                    :has-autograph="Boolean(settings.autographUrl)"
                    :model-value="settings.autographUrl ?? undefined"
                    @update:model-value="handleUpdateSignature"
                  />
                </div>
                <div
                  v-if="autographTooBigError"
                  class="text-error text-body-small mt-1"
                >
                  {{
                    i18n.t(
                      autographTooBigError.key,
                      autographTooBigError.params
                    )
                  }}
                </div>
              </div>

              <div>
                <v-text-field
                  id="optionalText"
                  v-model.trim="settings.optionalText"
                  density="compact"
                  :disabled="updatingState === 'loading'"
                  hide-details="auto"
                  :label="i18n.t('settings.labels.optional_text')"
                  :placeholder="i18n.t('settings.placeholders.optional_text')"
                  :rules="[optionalTextValidationRule]"
                  type="text"
                  variant="outlined"
                  @update:model-value="getPreviewDebounced()"
                />
              </div>
              <div>
                <v-text-field
                  id="location"
                  v-model.trim="settings.location"
                  class="form-input"
                  density="compact"
                  :disabled="updatingState === 'loading'"
                  hide-details="auto"
                  :label="i18n.t('settings.labels.localtion')"
                  :placeholder="i18n.t('settings.placeholders.location')"
                  :rules="[locationValidationRule]"
                  type="text"
                  variant="outlined"
                  @update:model-value="getPreviewDebounced()"
                />
              </div>
            </div>
          </v-form>
        </v-col>
      </v-row>
    </template>
    <template #actions>
      <VBtnSecondary
        :disabled="updatingState === 'loading' || preview.isLoading.value"
        variant="outlined"
        @click="() => (showDialog = false)"
      >
        {{ i18n.t('buttons.cancel') }}
      </VBtnSecondary>
      <VBtnPrimary
        color="primary"
        :disabled="preview.isLoading.value"
        :loading="updatingState === 'loading'"
        @click="handleSubmit"
      >
        {{ i18n.t('buttons.save') }}
      </VBtnPrimary>
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import OverlaySpinner from '@/components/settings/OverlaySpinner.vue';
import { onUnmounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAsyncState } from '@vueuse/core';
import { base64ToFile, debounce } from '@/composables/useUtils';
import { useEmitter } from '@/composables/useEmitter';
import { useMessageStore } from '@/stores/message.ts';
import { useSignStore } from '@/stores/sign.ts';
import { watch } from 'vue';
import { DeepDialog, DeepSignature } from '@deepcloud/deep-ui-lib';
import { useDisplay } from 'vuetify/framework';
import { useApi } from '@/composables/useApi.ts';
import {
  type AutographTooBigMessage,
  getAutographTooBigMessage
} from '@/utils/autographErrors.ts';

const { deepSignAPI } = useApi();
const i18n = useI18n();
const { mdAndDown } = useDisplay();

const messageStore = useMessageStore();
const emitter = useEmitter();
const signStore = useSignStore();

const showDialog = ref(false);
const updatingState = ref<'idle' | 'loading' | 'error'>('idle');
const settings = ref<{
  autographUrl: null | string;
  optionalText: string;
  location: string;
}>({
  autographUrl: null,
  optionalText: '',
  location: ''
});
const previewChanged = ref(false);
const autographChanged = ref(false);
// Last autograph value confirmed to be persisted in the BE - used to revert
// the displayed draft if a subsequent save attempt fails.
const savedAutographUrl = ref<string | null>(null);
const autographTooBigError = ref<AutographTooBigMessage | null>(null);

emitter.$on('open-signature-edition-dialog', () => openDialog());

onUnmounted(() =>
  emitter.$off(
    'open-signature-edition-dialog',
    () => (showDialog.value = false)
  )
);

function openDialog() {
  autographTooBigError.value = null;
  showDialog.value = true;
}

async function getPreviewPayload() {
  return {
    data: {
      sizeFactor: 2,
      optionalText: settings.value.optionalText,
      location: settings.value.location
    },
    autograph: settings.value.autographUrl
      ? await base64ToFile(settings.value.autographUrl, 'autograph')
      : undefined
  };
}

// Initialize settings state using useAsyncState to fetch data and set up preview
const settingsInitialState = reactive(
  useAsyncState(
    async () => {
      const response = await deepSignAPI.value.usersMe.get();
      const data = response.data;
      settings.value.location = data.location;
      settings.value.optionalText = data.optionalText;
      settings.value.autographUrl = await fetchImageAsBase64(data.autographUrl);
      savedAutographUrl.value = settings.value.autographUrl;
      return data;
    },
    {},
    {
      resetOnExecute: false,
      shallow: false,
      immediate: false,
      onSuccess: async () => preview.execute(0, await getPreviewPayload())
    }
  )
);

const locationValidationRule = (value: string) =>
  !value || value?.length <= 100 || i18n.t('settings.validation.location');

const optionalTextValidationRule = (value: string) =>
  !value || value?.length <= 100 || i18n.t('settings.validation.optional_text');

const preview = useAsyncState(
  async (payload: Awaited<ReturnType<typeof getPreviewPayload>>) => {
    return deepSignAPI.value.usersMeSignaturePreview
      .create(payload.data, payload.autograph)
      .then((t) => URL.createObjectURL(t.data));
  },
  '',
  {
    resetOnExecute: false,
    immediate: false,
    // The preview endpoint validates the autograph as well, so it is the first
    // call that can report an oversized image - surface it right away instead
    // of waiting for the save attempt.
    onError: async (error) => {
      autographTooBigError.value = await getAutographTooBigMessage(error);
    }
  }
);

const getPreviewDebounced = debounce(async () => {
  preview.execute(0, await getPreviewPayload());
  previewChanged.value = true;
}, 250);

async function fetchImageAsBase64(url: string) {
  if (!url) return null;
  const response = await fetch(url);
  const imageBlob = await response.blob();
  if (imageBlob) {
    return convertBlobToBase64(imageBlob);
  }
}

function convertBlobToBase64(blob: Blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

function handleUpdateSignature(newSignatureUrl: string | undefined) {
  settings.value.autographUrl = newSignatureUrl || null;
  autographChanged.value = true;
  autographTooBigError.value = null;
  getPreviewDebounced();
}

async function handleSubmit() {
  updatingState.value = 'loading';
  autographTooBigError.value = null;

  const [autographResult, settingsResult] = await Promise.allSettled([
    autographChanged.value
      ? settings.value.autographUrl
        ? saveAutograph(settings.value.autographUrl)
        : removeAutograph()
      : Promise.resolve(),
    previewChanged.value ? saveSettings(settings.value) : Promise.resolve()
  ]);

  const autographSaveFailed = autographResult.status === 'rejected';
  const settingsSaveFailed = settingsResult.status === 'rejected';

  if (autographSaveFailed) {
    // Keep displaying the last known-good autograph instead of the one that
    // just failed to save in the BE.
    settings.value.autographUrl = savedAutographUrl.value;
    getPreviewDebounced();

    autographTooBigError.value = await getAutographTooBigMessage(
      autographResult.reason
    );
  }

  if (autographTooBigError.value) {
    messageStore.showMessage({
      text: i18n.t(
        autographTooBigError.value.key,
        autographTooBigError.value.params
      ),
      color: 'error',
      icon: 'fa fa-circle-exclamation',
      timeout: 8000
    });
  } else if (autographSaveFailed || settingsSaveFailed) {
    messageStore.showMessage({
      key: 'snackbars.signature_updated_error',
      color: 'error',
      icon: 'fa fa-circle-exclamation',
      timeout: 2000
    });
  } else {
    messageStore.showMessage({
      key: 'snackbars.signature_updated_success',
      color: 'success',
      icon: 'fa fa-circle-check',
      timeout: 2000
    });
    if (signStore.document) {
      signStore.autographData = settings.value.autographUrl;
      signStore.document.signContext.defaultAutographUrl =
        settings.value.autographUrl;
    }
    savedAutographUrl.value = settings.value.autographUrl;
    emitter.$emit('retrigger-preview');
    showDialog.value = false;
  }

  updatingState.value = 'idle';
}

async function saveSettings(newSettings) {
  return deepSignAPI.value.usersMe.updateById(newSettings);
}

async function saveAutograph(autographUrl: string) {
  const autograph = await base64ToFile(autographUrl, 'autograph');
  return deepSignAPI.value.usersMeAutograph.create(autograph);
}

async function removeAutograph() {
  return deepSignAPI.value.usersMeAutograph.delete();
}

watch(
  () => showDialog.value,
  (dialogDisplayed) => {
    if (dialogDisplayed) {
      // Execute the initial fetch
      settingsInitialState.execute();
    }
  }
);
</script>

<style scoped lang="postcss">
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.preview-wrapper {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  aspect-ratio: 851/448;
}

.signature-preview-image {
  box-shadow:
    0 0 #0000,
    0 0 #0000,
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
}
</style>
